import { indexActionsByPublicId, isActionOfType } from 'flow/service/actions';
import { getBrowserOS } from 'flow/service/util';
import { ActionType } from 'flow/store/engine/constants';
import type {
  Action,
  ActionOption,
  ServiceObject,
  ServicesByName,
} from 'flow/store/engine/types';
import { WEBHOOK_KEY } from '../NodeEditorDrawer/ServiceTabs/constants';

/**
 * Checks if the event target is an input element (input, textarea, or contenteditable)
 */
export function isTargetInputElement(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;

  if (!target) {
    return false;
  }

  return target.tagName === 'INPUT'
    || target.tagName === 'TEXTAREA'
    || target.isContentEditable;
}

/**
 * Returns the keyboard shortcuts for copy and paste based on the user's operating system
 */
export function getKeyboardShortcuts(): { copyShortcut: string; pasteShortcut: string } {
  const browserOS = getBrowserOS();
  const isMac = browserOS === 'mac';

  return {
    copyShortcut: isMac ? '⌘ C' : 'Ctrl C',
    pasteShortcut: isMac ? '⌘ V' : 'Ctrl V',
  };
}

/**
 * Filters out auth/secret options and proprietary options from service action options
 */
export function getScrubbedServiceActionOptions(args: {
  options: Record<string, ActionOption>;
  service: ServiceObject;
}): Record<string, ActionOption> {
  const { options, service } = args;

  const matchingServiceConfigOptions = service?.serviceConfigOptions ?? [];
  const authOrSecretKeys = new Set(
    matchingServiceConfigOptions
      .filter(option => option.category === 'auth' || option.isSecret)
      .map(option => option.slug),
  );

  const scrubbedOptions = Object
    .entries(options)
    .filter(([name]) => {
      if (name === WEBHOOK_KEY) return false; // WEBHOOK_KEY is a proprietary option
      if (authOrSecretKeys.has(name)) return false;
      return true;
    });

  return Object.fromEntries(scrubbedOptions);
}

/**
 * removes specific service action options that are:
 * 1. proprietary to the account
 * 2. auth or secret
 */
export function scrubServiceActions(args: {
  actions: Record<string, Action>;
  services: ServicesByName;
}): Record<Action['publicId'], Action> {
  const { actions, services } = args;

  const scrubbedActions = Object.values(actions).map(action => {
    // do not modify non-service actions
    if (!isActionOfType(action, ActionType.SERVICE)) {
      return action;
    }

    return {
      ...action,
      options: getScrubbedServiceActionOptions({
        options: action.options,
        service: services[action.service.internalName],
      }),
    };
  });

  return indexActionsByPublicId(scrubbedActions);
}
